<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useNotificationsStore } from '@/stores/notifications'

// Hôte unique des messages éphémères, monté une seule fois dans App.vue. Les
// composants ne rendent jamais de toast eux-mêmes : ils poussent dans le store,
// ce qui garde l'empilement et les durées cohérents dans toute l'application.
const notifications = useNotificationsStore()
const { t } = useI18n()
</script>

<template>
  <!-- aria-live="polite" : le lecteur d'écran annonce le message sans couper ce
       qu'il est en train de lire. Sans cet attribut, un toast est purement
       visuel — donc invisible pour une partie des utilisateurs. -->
  <div class="toasts" role="status" aria-live="polite">
    <TransitionGroup name="toast">
      <div
        v-for="toast in notifications.toasts"
        :key="toast.id"
        class="toast"
        :class="`toast-${toast.kind}`"
      >
        <span class="toast-msg">{{ toast.message }}</span>
        <button
          class="toast-close"
          type="button"
          :aria-label="t('common.close')"
          @click="notifications.dismiss(toast.id)"
        >
          ×
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toasts {
  position: fixed;
  bottom: var(--space-6);
  right: var(--space-6);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  /* Le conteneur couvre un coin de l'écran en permanence : sans
     pointer-events:none il intercepterait les clics destinés à la page. Les
     toasts eux-mêmes les réactivent. */
  pointer-events: none;
  max-width: min(420px, calc(100vw - var(--space-6) * 2));
}

.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
  font-size: var(--text-base);
}

.toast-msg {
  flex: 1;
  /* Un message d'erreur d'API peut contenir un identifiant très long sans
     espace : sans cette règle il déborderait de la carte. */
  overflow-wrap: anywhere;
}

/* Bordure gauche colorée plutôt qu'un fond entièrement teinté : le texte reste
   lisible dans les deux thèmes sans recalculer un contraste par variante. */
.toast-success {
  border-left: 3px solid var(--color-success);
}
.toast-warning {
  border-left: 3px solid var(--color-warn);
}
.toast-error {
  border-left: 3px solid var(--color-danger);
}
.toast-info {
  border-left: 3px solid var(--color-primary);
}

.toast-close {
  border: 0;
  background: none;
  color: var(--color-text-subtle);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.toast-close:hover {
  color: var(--color-text);
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
